import React, { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { GetServerSideProps, NextPage } from "next";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";

import getStrain from "api/requests/getStrain";
import getStrainTags from "api/requests/getStrainTags";
import postStrainReview from "api/requests/postStrainReview";
import { Action, Category } from "constants/events";
import { useSnackBar } from "context/SnackBarContext";
import { Strain } from "custom-types/Strain";
import { StrainTag } from "custom-types/StrainTags";
import { useEventTracker } from "hooks/useEventTracker";
import useUserFavorites from "redux/hooks/useUserFavorites";
import { getCountryCode } from "redux/selectors/location";
import {
  getIsLoggedIn,
  getIsUserLoading,
  getUserId,
} from "redux/selectors/user";
import getStrainRedirect from "utils/getStrainRedirect";

import Button from "components/botanic/Button";
import Checkbox from "components/botanic/Checkbox";
import Radio from "components/botanic/Radio";
import ExpandingSectionWithCount from "components/ExpandingSectionWithCount";
import ArrowIcon from "components/Icons/arrows/ArrowIcon";
import Image from "components/Image";
import InteractiveStarRating from "components/InteractiveStarRating";
import MetaTags from "components/MetaTags";
import RadioGroup from "components/RadioGroup";
import SsoBumpoutModal from "components/SsoBumpoutModal";
import TileButton from "components/TileButton";

type ReviewForm = {
  form: string;
  isPrivate: boolean;
  rating: number;
  reportedBenefits: string[];
  reportedFeelings: string[];
  reportedFlavors: string[];
  reportedNegatives: string[];
  text: string;
};

type StrainTagWithCategory = StrainTag & {
  category: keyof ReviewForm;
};
type ReportedOptions = StrainTagWithCategory[];
type Props = {
  strain: Strain;
  reportedOptions: {
    effects: ReportedOptions;
    flavors: ReportedOptions;
    helpsWith: ReportedOptions;
  };
};

const WriteStrainReview: NextPage<Props> = ({ strain, reportedOptions }) => {
  const { publishEvent } = useEventTracker();

  const trackErrors = () => {
    publishEvent({
      action: Action.submit,
      category: Category.reviews,
      label: "Strain review error",
      strainId: strain.id,
      userId,
    });
  };

  const userIsLoggedIn = useSelector(getIsLoggedIn);
  const userIsLoading = useSelector(getIsUserLoading);
  const userId = useSelector(getUserId);

  const [showSsoModal, setShowSsoModal] = useState(false);
  useEffect(() => {
    if (!userIsLoading && !userIsLoggedIn) {
      publishEvent({
        action: Action.impression,
        category: Category.reviews,
        label: "Sign in prompt",
      });
      setShowSsoModal(true);
    }
  }, [userIsLoading, userIsLoggedIn]);

  const [review, setReview] = useState<ReviewForm>({
    form: "",
    isPrivate: false,
    rating: 0,
    reportedBenefits: [],
    reportedFeelings: [],
    reportedFlavors: [],
    reportedNegatives: [],
    text: "",
  });

  const toggleReview = (
    key: string,
    val: string | boolean | string[] | number,
  ) =>
    setReview({
      ...review,
      [key]: val,
    });

  const [errors, setErrors] = useState<Record<string, boolean>>({
    form: false,
    rating: false,
    text: false,
  });

  const toggleErrors = (error: string, value: boolean) =>
    setErrors({
      ...errors,
      [error]: value,
    });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const reviewTextRef = useRef<HTMLTextAreaElement>(null);

  const handleButtonSectionClick = (type: keyof ReviewForm, option: string) => {
    const existingType = review[type] as string[];
    const isSelected = existingType.indexOf(option) > -1;
    const value = isSelected
      ? existingType.filter((typeOption) => typeOption !== option)
      : [...existingType, option];

    toggleReview(type, value);
  };

  const validateReview = (reviewText?: string) => {
    let validated = true;
    if (reviewText && reviewText.length && reviewText.length < 10) {
      toggleErrors("text", true);
      validated = false;
    }
    if (!review.rating) {
      toggleErrors("rating", true);
      validated = false;
    }
    return validated;
  };

  const countryCode = useSelector(getCountryCode);

  const { addSnackBarCookie } = useSnackBar();

  const { useInitiateFavorites, checkIfUserFollows } = useUserFavorites();
  useInitiateFavorites();

  const returnToStrainpage = () => {
    router.push(`/strains/${strain.slug}`);
  };
  const redirectToStrainPageWithConfirmation = () =>
    router.push(`/strains/${strain.slug}?show_favorites_modal=true`);

  const submitReview = async () => {
    if (!userIsLoggedIn) {
      return;
    }

    const reviewText = reviewTextRef?.current?.value || "";
    const isValid = validateReview(reviewText);
    if (!isValid) {
      trackErrors();
      window.scrollTo(0, 0);
      return;
    }

    setIsSubmitting(true);

    const data = {
      form: review.form,
      is_private: review.isPrivate,
      language: "en",
      rating: review.rating,
      reported_benefits: review.reportedBenefits,
      reported_feelings: review.reportedFeelings,
      reported_flavors: review.reportedFlavors,
      reported_negatives: review.reportedNegatives,
      text: reviewText,
    };

    const { status } = await postStrainReview(strain.slug, data, countryCode);

    if (status === 201) {
      const userFavoritedStrain = checkIfUserFollows(strain.slug);
      if (!userFavoritedStrain && review.rating > 2) {
        publishEvent({
          action: Action.submit,
          category: Category.reviews,
          label: "Strain review",
          strainId: strain.id,
          userId,
        });
        redirectToStrainPageWithConfirmation();
      } else {
        publishEvent({
          action: Action.submit,
          category: Category.reviews,
          label: "Strain review",
          strainId: strain.id,
          userId,
        });
        addSnackBarCookie("reviewSubmitted");
        returnToStrainpage();
      }
    } else if (status === 401) {
      trackErrors();
      setShowSsoModal(true);
    } else {
      trackErrors();
      setIsSubmitting(false);
      toggleErrors("form", true);
    }
  };

  const setRating = (index: number) => {
    toggleReview("rating", index);
    toggleErrors("rating", false);
  };

  const router = useRouter();

  return (
    <>
      <MetaTags
        title={`Write a review for ${strain.name} | Leafly`}
        description={`Review ${strain.name} marijuana strain on Leafly.`}
        robots={`noindex,noarchive`}
      />
      <meta name="robots" content="noindex,noarchive" />
      <div className="container mb-xxl">
        <button
          type="button"
          className="text-green text-xs flex items-center mt-xl"
          onClick={returnToStrainpage}
        >
          <ArrowIcon width="16" height="16" direction="left" />
          <span className="ml-xs underline font-bold">
            Back to {strain.name}
          </span>
        </button>
        <div className="flex items-center mt-lg mb-xl">
          {strain.nugImage && (
            <div style={{ height: 60, width: 60 }}>
              <Image
                src={strain.nugImage}
                sizes={[60]}
                alt={`${strain.name} flower`}
                ratio="square"
              />
            </div>
          )}
          <h1 className="text-md">{strain.name}</h1>
        </div>
        <form>
          <div className="flex-col md:flex-row row mb-xxl">
            <div className="col md:col-1/2 lg:col-1/3 mb-xxl md:mb-none">
              <div
                className={classNames(
                  "p-xl shadow-low h-full flex flex-col justify-center rounded",
                  { "border-2 border-error": errors.rating },
                )}
              >
                <InteractiveStarRating
                  onSetRating={setRating}
                  initialRating={review.rating as number}
                  className="flex justify-center"
                  starSize={37}
                  legend="How many stars do you give it?"
                  legendClassName="font-normal text-sm text-center mb-xs"
                />
              </div>
              <div
                className={classNames("text-error text-xs mt-xs absolute", {
                  hidden: !errors.rating,
                })}
              >
                Please select a star rating
              </div>
            </div>
            <div className="col md:col-1/2 lg:col-1/3">
              <div className="py-xl px-xxl shadow-low rounded">
                <RadioGroup
                  name={"menuType"}
                  className="-mb-md row"
                  label="How did you hit it?"
                  labelClassName="font-normal text-sm text-center mb-sm"
                >
                  {["flower", "concentrate", "edible", "cartridge"].map(
                    (item) => (
                      <Radio
                        key={item}
                        id={item}
                        value={item}
                        checked={review.form === item}
                        label={item}
                        className="capitalize col-1/2 pl-none mb-md"
                        onChange={() => toggleReview("form", item)}
                        onClick={() => toggleReview("form", item)}
                        data-testid={`menu-search-type-option-${item}`}
                      />
                    ),
                  )}
                </RadioGroup>
              </div>
            </div>
          </div>
          {!!reportedOptions.effects.length && (
            <ExpandingSectionWithCount
              defaultOpen={true}
              title="Which effects did you feel?"
              subtitle="(Choose up to 4)"
              itemsSelected={
                review.reportedFeelings.length + review.reportedNegatives.length
              }
            >
              <div className="row mb-xxl">
                {reportedOptions.effects.map((effect) => {
                  const selected =
                    review.reportedNegatives.findIndex(
                      (reportedNegative) => reportedNegative === effect.slug,
                    ) > -1 ||
                    review.reportedFeelings.findIndex(
                      (reportedFeeling) => reportedFeeling === effect.slug,
                    ) > -1;
                  const hasReachedLimit =
                    review.reportedFeelings.length +
                      review.reportedNegatives.length >=
                    4;
                  const disabled = !selected && hasReachedLimit;
                  return (
                    <div className="col-1/3 md:col-1/6 mb-md" key={effect.slug}>
                      <TileButton
                        category="effects"
                        disabled={disabled}
                        name={effect.displayName}
                        slug={effect.slug}
                        isSelected={selected}
                        onClick={() =>
                          handleButtonSectionClick(effect.category, effect.slug)
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </ExpandingSectionWithCount>
          )}
          {!!reportedOptions.flavors.length && (
            <ExpandingSectionWithCount
              title="Describe the flavor &amp; smell?"
              subtitle="(Choose up to 4)"
              itemsSelected={review.reportedFlavors.length}
              shouldExpandContainer={true}
            >
              <div className="row">
                {reportedOptions.flavors.map((flavor) => {
                  const selected =
                    review.reportedFlavors.findIndex(
                      (reportedFlavor) => reportedFlavor === flavor.slug,
                    ) > -1;
                  const hasReachedLimit = review.reportedFlavors.length >= 4;
                  const disabled = !selected && hasReachedLimit;
                  return (
                    <div className="col-1/3 md:col-1/6 mb-md" key={flavor.slug}>
                      <TileButton
                        category="flavors"
                        disabled={disabled}
                        name={flavor.displayName}
                        isSelected={selected}
                        slug={flavor.slug}
                        onClick={() =>
                          handleButtonSectionClick(flavor.category, flavor.slug)
                        }
                      />
                    </div>
                  );
                })}
              </div>
            </ExpandingSectionWithCount>
          )}
          {!!reportedOptions.helpsWith.length && (
            <ExpandingSectionWithCount
              title="Helps with?"
              subtitle="(Choose up to 4)"
              itemsSelected={review.reportedBenefits.length}
              className="mb-xl"
              shouldExpandContainer={true}
            >
              <div className="row">
                {reportedOptions.helpsWith.map((item) => {
                  const selected =
                    review.reportedBenefits.findIndex(
                      (reportedBenefit) => reportedBenefit === item.slug,
                    ) > -1;
                  const hasReachedLimit = review.reportedBenefits.length >= 4;
                  const disabled = !selected && hasReachedLimit;
                  return (
                    <div
                      className="mb-md col md:col-1/2 lg:col-1/3"
                      key={item.slug}
                    >
                      <Checkbox
                        onChange={() =>
                          handleButtonSectionClick(item.category, item.slug)
                        }
                        className={classNames("checkbox__label", {
                          "opacity-50 cursor-default": disabled,
                        })}
                        id={`write-review-checkbox-benefit-${item.slug}`}
                        disabled={disabled}
                        checked={selected}
                      >
                        {item.displayName}
                      </Checkbox>
                    </div>
                  );
                })}
              </div>
            </ExpandingSectionWithCount>
          )}
          <section>
            <h2 className="font-bold text-md">Any additional thoughts?</h2>
            <p>
              Your input matters and helps to shape our cannabis community here
              at Leafly.{" "}
            </p>
            <textarea
              ref={reviewTextRef}
              id="review-text-input"
              placeholder="Share your experience"
              rows={8}
              className={classNames(
                "border border-light-grey rounded overflow-hidden w-full p-lg bg-leafly-white",
                {
                  "border-error": errors.text,
                },
              )}
              style={{ resize: "none" }}
            />
            <div
              className={classNames("text-error text-xs", {
                hidden: !errors.text,
              })}
            >
              Minimum of 10 characters required
            </div>
          </section>
          <section className="my-lg">
            <Checkbox
              onChange={() => toggleReview("isPrivate", !review.isPrivate)}
              checked={review.isPrivate}
              id="write-review-checkbox-mark-private"
            >
              Make review private
            </Checkbox>
          </section>

          <div className="p-sm border border-light-grey rounded">
            <p className="text-xs text-grey mb-none italic">
              All reviews will be verified after submission; please give us time
              before they show up on the page. Thank you.
            </p>
          </div>

          <div className="md:flex flex-row-reverse justify-end mt-xxl">
            <Button
              disabled={isSubmitting || !userIsLoggedIn}
              variant="primary"
              onClick={(e: React.FormEvent) => {
                e.preventDefault();
                submitReview();
              }}
            >
              {isSubmitting ? "submitting" : "submit review"}
            </Button>
            <button
              type="button"
              onClick={() => {
                publishEvent({
                  action: Action.click,
                  category: Category.reviews,
                  label: "Strain review cancel",
                  strainId: strain.id,
                  userId,
                });
                returnToStrainpage();
              }}
              className="block md:mx-none mt-lg md:mt-none mx-auto px-xxl underline"
            >
              cancel
            </button>
          </div>

          {errors.form && (
            <p className="text-error mt-xl">
              An error has occurred, please try again later.
            </p>
          )}
        </form>
      </div>

      {!userIsLoggedIn && (
        <div className="fixed top-0 right-0 w-full h-full bg-white opacity-75 z-10" />
      )}

      {showSsoModal && (
        <SsoBumpoutModal
          closeModal={() => router.back()}
          title="You need to be logged in to leave a review."
          trackSignIn={() =>
            publishEvent({
              action: Action.click,
              category: Category.reviews,
              label: "Sign in",
            })
          }
          trackSignUp={() =>
            publishEvent({
              action: Action.click,
              category: Category.reviews,
              label: "Sign up",
            })
          }
        />
      )}
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { query } = context;
  const slug = Array.isArray(query?.strainSlug)
    ? query.strainSlug[0]
    : query.strainSlug || "";

  const redirect = getStrainRedirect(
    slug,
    `/strains/${slug}/reviews/write-review`,
  );

  if (redirect) {
    return {
      redirect,
    };
  }

  const strain = await getStrain(slug);

  if (!strain) {
    return {
      notFound: true,
    };
  }

  try {
    const {
      effects = [],
      negatives = [],
      flavors = [],
      conditions = [],
      symptoms = [],
    } = await getStrainTags();

    const categorizedEffects = effects.map((effect) => ({
      ...effect,
      ...{ category: "reportedFeelings" },
    }));

    const categorizedNegatives = negatives.map((negative) => ({
      ...negative,
      ...{ category: "reportedNegatives" },
    }));

    const reportedOptions = {
      effects: categorizedEffects
        .concat(categorizedNegatives)
        .sort((a, b) => (a.displayName < b.displayName ? -1 : 1)),
      flavors: flavors.map((flavor) => ({
        ...flavor,
        ...{ category: "reportedFlavors" },
      })),
      helpsWith: conditions
        .concat(symptoms)
        .map((condition) => ({
          ...condition,
          ...{ category: "reportedBenefits" },
        }))
        .sort((a, b) => (a.displayName < b.displayName ? -1 : 1)),
    };

    return { props: { reportedOptions, strain } };
  } catch (error) {
    return { notFound: true };
  }
};

export default WriteStrainReview;
